import { useState, useEffect, useCallback } from 'react'
import { Box, Flex, Text, Modal, ConfirmDialog, Grid, useT } from '@minetrace/ui'
import { useFormColors, FormSection, withFormProvider, TrackedInput, TrackedDatePicker, FileInput } from '@minetrace/ui/forms'
import {
  LuShieldCheck as ShieldCheck,
  LuPlus as Plus,
  LuTrash2 as Trash2,
} from 'react-icons/lu'
import type { UseFormReturn } from 'react-hook-form'
import type { PowerValidityResponse, CompanyPersonResponse } from '../../api/models/response/proveedor'
import type { AddPowerValidityRequest } from '../../api/models/request/proveedor'
import { useStep1PowerValidityForm, type PowerValidityFormValues } from './steps/useStep1PowerValidityForm'
import DocumentoDescarga from '../../components/DocumentoDescarga'
import { useDeleteWarning } from '../../lib/modificationMode'

// Misma constante que el validator/endpoint (appsettings → Validation:VigenciaPoder:ValidityDays)
const VALIDITY_DAYS = 10

interface FormInnerProps {
  formMethods: UseFormReturn<PowerValidityFormValues>
  isBusy: boolean
  onSave: (values: PowerValidityFormValues, documentFile: File) => void
  onRegisterSubmit?: (fn: () => void) => void
}

const PowerValidityFormInner = withFormProvider<PowerValidityFormValues, FormInnerProps>(
  ({ formMethods, isBusy, onSave, onRegisterSubmit }) => {
    const { t } = useT()
    const [documentFile, setDocumentFile] = useState<File | null>(null)
    const [fileError, setFileError] = useState<string | undefined>(undefined)

    const handleFormSubmit = useCallback(async () => {
      const isValid = await formMethods.trigger()
      if (!documentFile) {
        setFileError(t('proveedores.wizard.step1.powerValidityDocRequired'))
      } else {
        setFileError(undefined)
      }
      if (!isValid || !documentFile) return
      onSave(formMethods.getValues(), documentFile)
    }, [formMethods, documentFile, onSave, t])

    useEffect(() => {
      onRegisterSubmit?.(() => handleFormSubmit())
    }, [onRegisterSubmit, handleFormSubmit])

    const today = new Date().toISOString().split('T')[0]

    return (
      <Box opacity={isBusy ? 0.5 : 1} pointerEvents={isBusy ? 'none' : 'auto'} transition="opacity 0.2s">
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
          <TrackedInput name="code" labelTid="proveedores.wizard.step1.powerValidityCodeLabel" placeholderTid="proveedores.wizard.step1.powerValidityCodePlaceholder" maxLength={100} />
          <TrackedDatePicker name="issueDate" labelTid="proveedores.wizard.step1.powerValidityIssueDateLabel" maxDate={today} />
        </Grid>
        <Grid templateColumns="1fr" gap={4} mt={4}>
          <FileInput
            labelTid="proveedores.wizard.step1.powerValidityDocLabel"
            required
            accept=".pdf"
            value={documentFile}
            onChange={(f) => { setDocumentFile(f); setFileError(undefined) }}
            error={fileError}
          />
        </Grid>
      </Box>
    )
  },
  useStep1PowerValidityForm,
)

interface PowerValiditiesSectionProps {
  mode: 'edit' | 'readonly'
  ruc: string | null | undefined
  persons: CompanyPersonResponse[]
  powerValidities: PowerValidityResponse[]
  onAdd?: (companyPersonId: string, data: AddPowerValidityRequest, documentFile: File) => Promise<void>
  onRemove?: (powerValidityId: string) => Promise<void>
}

export default function PowerValiditiesSection({
  mode,
  ruc,
  persons,
  powerValidities,
  onAdd,
  onRemove,
}: PowerValiditiesSectionProps) {
  const c = useFormColors()
  const { t } = useT()
  const isReadonly = mode === 'readonly' || !onAdd

  const [modalOpen, setModalOpen] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const [submitFn, setSubmitFn] = useState<(() => void) | null>(null)
  const deleteWarning = useDeleteWarning()
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null)

  const isPJ = !!ruc && ruc.startsWith('20')
  // ContactType.RepresentanteLegal = 1
  // En una solicitud de modificación, el representante recién agregado (fila "staged-")
  // es al que corresponde cargar la vigencia; antes siempre se tomaba el anterior.
  const legalRep = persons.find(p => p.contactType === 1 && p.id.startsWith('staged-'))
    ?? persons.find(p => p.contactType === 1)

  if (!isPJ) {
    return null // sección no aplica a persona natural
  }

  const handleSave = async (values: PowerValidityFormValues, documentFile: File) => {
    if (!onAdd || !legalRep) return
    setIsBusy(true)
    try {
      await onAdd(legalRep.id, { code: values.code, issueDate: values.issueDate }, documentFile)
      setModalOpen(false)
    } finally { setIsBusy(false) }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !onRemove) return
    setIsBusy(true)
    try { await onRemove(deleteTarget.id); setDeleteTarget(null) } finally { setIsBusy(false) }
  }

  return (
    <FormSection
      icon={ShieldCheck}
      titleTid="proveedores.wizard.step1.powerValidityTitle"
      descriptionTid="proveedores.wizard.step1.powerValidityDescription"
    >
      {!legalRep ? (
        <Box p={4} textAlign="center" border="1px dashed" borderColor="gray.200" borderRadius="xl">
          <Text tid="proveedores.wizard.step1.powerValidityNoLegalRep" fontSize="sm" color={c.textMuted} />
        </Box>
      ) : (
        <>
          <Flex mb={3} justifyContent="space-between" alignItems="center">
            <Text fontSize="sm" color={c.textMuted}>
              <Text as="span" fontWeight="600" color={c.text}>{legalRep.fullName}</Text>
              {' — '}
              {t('proveedores.wizard.step1.powerValidityValidityHint', { days: VALIDITY_DAYS })}
            </Text>
            {!isReadonly && (
              <Box as="button" type="button" display="flex" alignItems="center" gap={2}
                   px={4} py={2} bg={c.accent} color="white" borderRadius="md" fontSize="sm" fontWeight="600"
                   cursor="pointer" _hover={{ opacity: 0.9 }} onClick={() => setModalOpen(true)}>
                <Plus size={14} />{t('proveedores.wizard.step1.powerValidityAddButton')}
              </Box>
            )}
          </Flex>

          {powerValidities.length === 0 ? (
            <Box p={6} textAlign="center" border="1px dashed" borderColor="gray.200" borderRadius="xl">
              <ShieldCheck size={28} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
              <Text tid="proveedores.wizard.step1.powerValidityEmpty" fontSize="sm" color={c.textMuted} />
            </Box>
          ) : (
            <Box overflowX="auto">
              <Box as="table" w="100%" fontSize="sm">
                <Box as="thead">
                  <Box as="tr">
                    {[
                      t('proveedores.wizard.step1.powerValidityColCode'),
                      t('proveedores.wizard.step1.powerValidityColPerson'),
                      t('proveedores.wizard.step1.powerValidityColIssue'),
                      t('proveedores.wizard.step1.powerValidityColExpires'),
                      t('proveedores.wizard.step1.powerValidityColStatus'),
                      t('proveedores.wizard.step1.powerValidityColFile'),
                      ...(isReadonly ? [] : ['']),
                    ].map(col => (
                      <Box as="th" key={col} textAlign="left" px={3} py={2} color={c.textMuted} fontWeight="600" fontSize="xs" borderBottom="1px solid" borderColor={c.border}>{col}</Box>
                    ))}
                  </Box>
                </Box>
                <Box as="tbody">
                  {powerValidities.map(r => (
                    <Box as="tr" key={r.id} _hover={{ bg: c.bgHover }}>
                      <Box as="td" px={3} py={2} color={c.text}>{r.code}</Box>
                      <Box as="td" px={3} py={2} color={c.text}>{r.personFullName ?? '—'}</Box>
                      <Box as="td" px={3} py={2} color={c.text}>{r.issueDate}</Box>
                      <Box as="td" px={3} py={2} color={c.text}>{r.expirationDate}</Box>
                      <Box as="td" px={3} py={2}>
                        <Box as="span" px={2} py={0.5} fontSize="xs" fontWeight="700" borderRadius="md"
                             bg={r.isValid ? 'green.100' : 'red.100'} color={r.isValid ? 'green.700' : 'red.700'}>
                          {r.isValid ? t('proveedores.wizard.step1.powerValidityStatusValid') : t('proveedores.wizard.step1.powerValidityStatusExpired')}
                        </Box>
                      </Box>
                      <Box as="td" px={3} py={2} color={c.text}>
                        {r.documentFile ? (
                          <DocumentoDescarga caption="Vigencia de Poder" storagePath={r.documentFile.storagePath} fileName={r.documentFile.originalFileName} />
                        ) : <Text tid="proveedores.wizard.step1.powerValidityNoFile" fontSize="sm" color={c.textMuted} />}
                      </Box>
                      {!isReadonly && (
                        <Box as="td" px={3} py={2}>
                          <Box as="button" type="button" display="flex" alignItems="center" gap={1} px={3} py={1.5}
                               bg="red.50" color="red.600" borderRadius="lg" fontSize="xs" fontWeight="600"
                               cursor="pointer" _hover={{ bg: 'red.100' }}
                               onClick={() => setDeleteTarget({ id: r.id, label: r.code })}>
                            <Trash2 size={12} />{t('proveedores.wizard.step1.powerValidityDelete')}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('proveedores.wizard.step1.powerValidityModalTitle')}
        subtitle={legalRep?.fullName ?? undefined}
        size="lg"
        footer={
          <>
            <Box as="button" type="button" px={5} py={2.5} borderRadius="md" fontSize="sm" fontWeight="600"
                 color={c.textMuted} border="1px solid" borderColor={c.border}
                 cursor="pointer" _hover={{ bg: c.bgHover }} onClick={() => setModalOpen(false)}>
              {t('proveedores.wizard.step1.powerValidityCancel')}
            </Box>
            <Box as="button" type="button" display="flex" alignItems="center" gap={2}
                 px={6} py={2.5} bg={c.accent} color="white" borderRadius="md" fontSize="sm" fontWeight="600"
                 cursor={isBusy ? 'not-allowed' : 'pointer'} opacity={isBusy ? 0.6 : 1}
                 _hover={{ opacity: 0.9 }} onClick={() => submitFn?.()} disabled={isBusy}>
              {isBusy ? t('proveedores.wizard.step1.powerValiditySaving') : <><Plus size={14} /> {t('proveedores.wizard.step1.powerValidityRegister')}</>}
            </Box>
          </>
        }
      >
        <PowerValidityFormInner
          isBusy={isBusy}
          onSave={handleSave}
          onRegisterSubmit={(fn) => setSubmitFn(() => fn)}
        />
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={t('proveedores.wizard.step1.powerValidityDeleteTitle')}
        confirmLabel={t('proveedores.wizard.step1.powerValidityDeleteConfirm')}
        variant="danger"
        isLoading={isBusy}
      >
        ¿Está seguro que desea eliminar la vigencia de poder <Text as="span" fontWeight="600">{deleteTarget?.label}</Text>? {deleteWarning}
      </ConfirmDialog>
    </FormSection>
  )
}
